<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Register Pregnancy</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Woman -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Woman to Register</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchWomen" type="text"
                         class="form-control" placeholder="Search by name, CBHI number, or phone...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Age Filter -->
              <div class="mb-3">
                <div class="btn-group btn-group-sm" role="group">
                  <input type="radio" class="btn-check" name="ageFilter" id="all-women"
                         value="all" v-model="ageFilter" @change="filterWomen">
                  <label class="btn btn-outline-primary" for="all-women">All Women</label>

                  <input type="radio" class="btn-check" name="ageFilter" id="reproductive-age"
                         value="reproductive" v-model="ageFilter" @change="filterWomen">
                  <label class="btn btn-outline-primary" for="reproductive-age">15-49 Years</label>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Select Woman</h6>
                <div class="list-group">
                  <div v-for="woman in searchResults" :key="woman.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedWoman?.member_id === woman.member_id}"
                       @click="selectWoman(woman)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ woman.first_name }} {{ woman.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Age: {{ calculateAge(woman.date_of_birth) }} years •
                          {{ woman.household_number }}
                        </p>
                        <small v-if="woman.phone_number" class="text-muted">
                          <i class="bx bx-phone"></i> {{ woman.phone_number }}
                        </small>
                      </div>
                      <div>
                        <span v-if="woman.enrollment_type === 'cbhi_member'"
                              class="badge bg-success">CBHI</span>
                        <span v-if="woman.has_active_pregnancy"
                              class="badge bg-danger ms-1">Already Pregnant</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No women found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Pregnancy Details -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <h6 class="mb-3">Pregnancy Information</h6>

              <!-- Woman Info -->
              <div class="alert alert-info">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="alert-heading">{{ selectedWoman.first_name }} {{ selectedWoman.surname }}</h6>
                    <p class="mb-0">
                      Age: {{ calculateAge(selectedWoman.date_of_birth) }} years<br>
                      CBHI: {{ selectedWoman.cbhi_number || 'Not enrolled' }}
                    </p>
                  </div>
                  <div class="text-end">
                    <button @click="previousStep" class="btn btn-sm btn-secondary">
                      <i class="bx bx-arrow-back"></i> Change Woman
                    </button>
                  </div>
                </div>
              </div>

              <!-- Pregnancy Form -->
              <div class="row">
                <!-- LMP Date -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Last Menstrual Period (LMP) <span class="text-danger">*</span></label>
                  <input v-model="pregnancyForm.lmpDate" @change="calculateEDD" type="date"
                         class="form-control" :max="today" required>
                  <small class="text-muted">First day of last menstrual period</small>
                </div>

                <!-- EDD -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Expected Delivery Date (EDD)</label>
                  <input v-model="pregnancyForm.eddDate" type="date" class="form-control" readonly>
                  <small class="text-muted">Automatically calculated (LMP + 280 days)</small>
                </div>

                <!-- Gravida -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">Gravida <span class="text-danger">*</span></label>
                  <input v-model.number="pregnancyForm.gravida" type="number"
                         class="form-control" min="1" required>
                  <small class="text-muted">Total pregnancies including current</small>
                </div>

                <!-- Para -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">Para <span class="text-danger">*</span></label>
                  <input v-model.number="pregnancyForm.para" type="number"
                         class="form-control" min="0" :max="pregnancyForm.gravida - 1" required>
                  <small class="text-muted">Previous deliveries ≥28 weeks</small>
                </div>

                <!-- Living Children -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">Living Children</label>
                  <input v-model.number="pregnancyForm.livingChildren" type="number"
                         class="form-control" min="0" :max="pregnancyForm.para">
                  <small class="text-muted">Currently living children</small>
                </div>

                <!-- Previous Pregnancy Complications -->
                <div class="col-12 mb-3">
                  <label class="form-label">Previous Pregnancy Complications</label>
                  <div class="row g-2">
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="stillbirth"
                               v-model="pregnancyForm.previousComplications" id="stillbirth">
                        <label class="form-check-label" for="stillbirth">Stillbirth</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="cesarean"
                               v-model="pregnancyForm.previousComplications" id="cesarean">
                        <label class="form-check-label" for="cesarean">Cesarean Section</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="hemorrhage"
                               v-model="pregnancyForm.previousComplications" id="hemorrhage">
                        <label class="form-check-label" for="hemorrhage">Hemorrhage</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="preeclampsia"
                               v-model="pregnancyForm.previousComplications" id="preeclampsia">
                        <label class="form-check-label" for="preeclampsia">Pre-eclampsia</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="abortion"
                               v-model="pregnancyForm.previousComplications" id="abortion">
                        <label class="form-check-label" for="abortion">Abortion/Miscarriage</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="other"
                               v-model="pregnancyForm.previousComplications" id="other">
                        <label class="form-check-label" for="other">Other</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Current Risk Factors -->
                <div class="col-12 mb-3">
                  <label class="form-label">Current Risk Factors</label>
                  <div class="row g-2">
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="age_under_20"
                               v-model="pregnancyForm.riskFactors" id="age_under_20">
                        <label class="form-check-label" for="age_under_20">{{'Age < 20'}}</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="age_over_35"
                               v-model="pregnancyForm.riskFactors" id="age_over_35">
                        <label class="form-check-label" for="age_over_35">Age > 35</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="height_under_150"
                               v-model="pregnancyForm.riskFactors" id="height_under_150">
                        <label class="form-check-label" for="height_under_150">{{'Height < 150cm'}}</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="diabetes"
                               v-model="pregnancyForm.riskFactors" id="diabetes">
                        <label class="form-check-label" for="diabetes">Diabetes</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="hypertension"
                               v-model="pregnancyForm.riskFactors" id="hypertension">
                        <label class="form-check-label" for="hypertension">Hypertension</label>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" value="multiple_pregnancy"
                               v-model="pregnancyForm.riskFactors" id="multiple_pregnancy">
                        <label class="form-check-label" for="multiple_pregnancy">Multiple Pregnancy</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Delivery Plan -->
                <div class="col-md-6 mb-3">
                  <label class="form-label">Planned Delivery Location</label>
                  <select v-model="pregnancyForm.deliveryPlan" class="form-select">
                    <option value="health_center">Health Center</option>
                    <option value="hospital">Hospital</option>
                    <option value="home_skilled">Home with Skilled Attendant</option>
                    <option value="undecided">Undecided</option>
                  </select>
                </div>

                <!-- High Risk -->
                <div class="col-md-6 mb-3">
                  <div class="alert" :class="isHighRisk ? 'alert-danger' : 'alert-success'">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" v-model="pregnancyForm.highRisk"
                             id="highRiskCheck" :disabled="autoHighRisk">
                      <label class="form-check-label" for="highRiskCheck">
                        <strong>High Risk Pregnancy</strong>
                        <br>
                        <small v-if="autoHighRisk">
                          Automatically marked due to risk factors
                        </small>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 3: First ANC Visit -->
          <div v-else-if="step === 3">
            <div class="mb-4">
              <h6 class="mb-3">Schedule First ANC Visit</h6>

              <!-- Summary -->
              <div class="alert alert-success">
                <h6 class="alert-heading">Pregnancy Summary</h6>
                <p class="mb-0">
                  <strong>{{ selectedWoman.first_name }} {{ selectedWoman.surname }}</strong><br>
                  LMP: {{ formatDate(pregnancyForm.lmpDate) }}<br>
                  EDD: {{ formatDate(pregnancyForm.eddDate) }}<br>
                  Current Gestation: {{ currentGestationWeeks }} weeks<br>
                  <span v-if="isHighRisk" class="text-danger">
                    <i class="bx bx-error-circle"></i> HIGH RISK PREGNANCY
                  </span>
                </p>
              </div>

              <!-- ANC Visit Schedule -->
              <div class="mb-3">
                <label class="form-label">First ANC Visit Date <span class="text-danger">*</span></label>
                <input v-model="ancForm.firstVisitDate" type="date" class="form-control"
                       :min="tomorrow" required>
                <small class="text-muted">
                  WHO recommends first ANC visit before 12 weeks
                  <span v-if="currentGestationWeeks > 12" class="text-warning">
                    (Currently {{ currentGestationWeeks }} weeks)
                  </span>
                </small>
              </div>

              <!-- Visit Time -->
              <div class="mb-3">
                <label class="form-label">Preferred Time</label>
                <select v-model="ancForm.preferredTime" class="form-select">
                  <option value="morning">Morning (8:00 - 12:00)</option>
                  <option value="afternoon">Afternoon (12:00 - 17:00)</option>
                  <option value="anytime">Any Time</option>
                </select>
              </div>

              <!-- SMS Reminder -->
              <div class="mb-3" v-if="selectedWoman.phone_number">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="ancForm.sendSmsReminder"
                         id="smsReminder">
                  <label class="form-check-label" for="smsReminder">
                    Send SMS reminder to {{ selectedWoman.phone_number }}
                  </label>
                </div>
              </div>

              <!-- Notes -->
              <div class="mb-3">
                <label class="form-label">Notes</label>
                <textarea v-model="ancForm.notes" class="form-control" rows="2"
                          placeholder="Any additional notes..."></textarea>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="checkExistingPregnancy" :disabled="!selectedWoman || loading">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-primary"
                  @click="nextStep" :disabled="!isFormValid">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="registerPregnancy"
                    :disabled="!ancForm.firstVisitDate || submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Registering...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Register Pregnancy
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "PregnancyRegistrationModal",
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Woman selection
      searchQuery: '',
      searchResults: [],
      selectedWoman: null,
      ageFilter: 'reproductive',

      // Pregnancy form
      pregnancyForm: {
        lmpDate: '',
        eddDate: '',
        gravida: 1,
        para: 0,
        livingChildren: 0,
        previousComplications: [],
        riskFactors: [],
        deliveryPlan: 'health_center',
        highRisk: false
      },

      // ANC scheduling
      ancForm: {
        firstVisitDate: '',
        preferredTime: 'morning',
        sendSmsReminder: true,
        notes: ''
      },

      today: new Date().toISOString().split('T')[0],
      tomorrow: new Date(Date.now() + 86400000).toISOString().split('T')[0]
    };
  },

  computed: {
    isFormValid() {
      return this.pregnancyForm.lmpDate &&
          this.pregnancyForm.gravida > 0 &&
          this.pregnancyForm.para >= 0 &&
          this.pregnancyForm.para < this.pregnancyForm.gravida;
    },

    autoHighRisk() {
      // Auto-detect high risk based on factors
      const highRiskFactors = ['age_under_20', 'age_over_35', 'multiple_pregnancy',
        'diabetes', 'hypertension'];
      const hasHighRiskFactor = this.pregnancyForm.riskFactors.some(rf =>
          highRiskFactors.includes(rf)
      );
      const hasComplication = this.pregnancyForm.previousComplications.length > 0;

      return hasHighRiskFactor || hasComplication;
    },

    isHighRisk() {
      return this.pregnancyForm.highRisk || this.autoHighRisk;
    },

    currentGestationWeeks() {
      if (!this.pregnancyForm.lmpDate) return 0;

      const lmp = new Date(this.pregnancyForm.lmpDate);
      const today = new Date();
      const diffTime = Math.abs(today - lmp);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return Math.floor(diffDays / 7);
    }
  },

  watch: {
    autoHighRisk(value) {
      if (value) {
        this.pregnancyForm.highRisk = true;
      }
    }
  },

  mounted() {
    this.searchWomen();
  },

  methods: {
    async searchWomen() {
      if (!this.searchQuery && this.ageFilter === 'all') {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery,
        "searchType": "all"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          let results = res.data.data.members || [];

          // Filter for women only
          results = results.filter(m => m.gender === 'female');

          // Apply age filter if reproductive age selected
          if (this.ageFilter === 'reproductive') {
            results = results.filter(m => {
              const age = this.calculateAge(m.date_of_birth);
              return age >= 15 && age <= 49;
            });
          }

          this.searchResults = results;
        }
      } catch (e) {
        console.error("Failed to search women:", e);
      } finally {
        this.loading = false;
      }
    },

    filterWomen() {
      this.searchWomen();
    },

    selectWoman(woman) {
      this.selectedWoman = woman;

      // Auto-check age risk factors
      const age = this.calculateAge(woman.date_of_birth);
      if (age < 20 && !this.pregnancyForm.riskFactors.includes('age_under_20')) {
        this.pregnancyForm.riskFactors.push('age_under_20');
      }
      if (age > 35 && !this.pregnancyForm.riskFactors.includes('age_over_35')) {
        this.pregnancyForm.riskFactors.push('age_over_35');
      }
    },

    async checkExistingPregnancy() {
      if (!this.selectedWoman) return;

      // In a real implementation, check if woman has active pregnancy
      if (this.selectedWoman.has_active_pregnancy) {
        alert('This woman already has an active pregnancy registered.');
        return;
      }

      this.step = 2;
    },

    calculateEDD() {
      if (!this.pregnancyForm.lmpDate) return;

      const lmp = new Date(this.pregnancyForm.lmpDate);
      const edd = new Date(lmp.getTime() + (280 * 24 * 60 * 60 * 1000)); // Add 280 days
      this.pregnancyForm.eddDate = edd.toISOString().split('T')[0];
    },

    nextStep() {
      if (this.step === 2 && this.isFormValid) {
        this.step = 3;
      }
    },

    previousStep() {
      if (this.step === 3) {
        this.step = 2;
      } else if (this.step === 2) {
        this.step = 1;
      }
    },

    async registerPregnancy() {
      if (!this.ancForm.firstVisitDate || !this.selectedWoman || !this.isFormValid) return;

      this.submitting = true;

      const data = {
        "function": "registerPregnancy",
        "memberId": this.selectedWoman.member_id,
        "lmpDate": this.pregnancyForm.lmpDate,
        "eddDate": this.pregnancyForm.eddDate,
        "gravida": this.pregnancyForm.gravida,
        "para": this.pregnancyForm.para,
        "livingChildren": this.pregnancyForm.livingChildren,
        "riskFactors": [
          ...this.pregnancyForm.riskFactors,
          ...this.pregnancyForm.previousComplications
        ].join(', '),
        "highRisk": this.isHighRisk,
        "deliveryPlan": this.pregnancyForm.deliveryPlan
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Schedule first ANC visit
          const scheduleData = {
            "function": "createWorkSchedule",
            "householdId": this.selectedWoman.household_id,
            "scheduledDate": this.ancForm.firstVisitDate,
            "activityType": "anc_visit",
            "activityDescription": `First ANC visit - ${this.selectedWoman.first_name} ${this.selectedWoman.surname}`,
            "priority": this.isHighRisk ? "high" : "medium",
            "notes": this.ancForm.notes
          };

          await executeJson(scheduleData, authUrl);

          this.$emit('registered');
        } else {
          alert(res.data.message || 'Failed to register pregnancy');
        }
      } catch (e) {
        console.error("Failed to register pregnancy:", e);
        alert('Failed to register pregnancy. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.btn-check:checked + .btn-outline-primary {
  background-color: #556ee6;
  border-color: #556ee6;
  color: white;
}

.form-check-input:disabled {
  opacity: 0.5;
}

.alert-danger .form-check-label {
  color: #842029;
}

.alert-success .form-check-label {
  color: #0f5132;
}
</style>